import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type EventItem = {
  id: string;
  title: string;
  type: string;
  location: string | null;
  date: string;
  time: string | null;
  endTime: string | null;
  description: string;
};

type MunnaMenzaParticipant = {
  name: string;
  notes: string;
};

type MunnaMenzaEvent = {
  eventId: string;
  date: string;
  participants: MunnaMenzaParticipant[];
};

type ApplicationForm = {
  name: string;
  notes: string;
  sending: boolean;
  error: string;
  success: boolean;
};

const EVENTS_URL =
  "https://sopronpogo.blob.core.windows.net/pogo/events.json";

const MENZA_GET_URL =
  "https://sopronpogo-api.greenmeadow-9f8cab91.polandcentral.azurecontainerapps.io/api/munnamenza/get";

const MENZA_ADD_URL =
  "https://sopronpogo-api.greenmeadow-9f8cab91.polandcentral.azurecontainerapps.io/api/munnamenza/add";

const TZ = "Europe/Budapest";
const HOUR = 60 * 60 * 1000;

const EMPTY_FORM: ApplicationForm = {
  name: "",
  notes: "",
  sending: false,
  error: "",
  success: false,
};

// Interpret event times in Budapest, regardless of the browser's time zone.
const wallFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function wallMilliseconds(instant: number): number {
  const parts = wallFormatter.formatToParts(new Date(instant));

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value);

  return Date.UTC(
    value("year"),
    value("month") - 1,
    value("day"),
    value("hour"),
    value("minute"),
    value("second")
  );
}

function eventDateTime(date: string, time: string | null): Date | null {
  if (!time || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

  const midnight = Date.parse(`${date}T00:00:00Z`);

  if (
    !Number.isFinite(midnight) ||
    new Date(midnight).toISOString().slice(0, 10) !== date
  ) {
    return null;
  }

  if (
    time !== "24:00" &&
    !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)
  ) {
    return null;
  }

  const [hours, minutes] = time.split(":").map(Number);
  const wall = midnight + hours * HOUR + minutes * 60_000;

  const offsets = new Set(
    [-24 * HOUR, 0, 24 * HOUR].map(
      delta => wallMilliseconds(wall + delta) - (wall + delta)
    )
  );

  const matches = Array.from(offsets)
    .map(offset => wall - offset)
    .filter(instant => wallMilliseconds(instant) === wall);

  // Reject nonexistent or ambiguous daylight-saving times.
  return matches.length === 1 ? new Date(matches[0]) : null;
}

export default function Events() {
  const { t, i18n } = useTranslation();

  const locale = i18n.language.startsWith("de")
    ? "de-DE"
    : i18n.language.startsWith("hu")
      ? "hu-HU"
      : "en-US";

  const [events, setEvents] = useState<EventItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());

  const [menzaEvents, setMenzaEvents] = useState<MunnaMenzaEvent[]>([]);
  const [menzaLoaded, setMenzaLoaded] = useState(false);
  const [menzaError, setMenzaError] = useState(false);
  const [reloadMenza, setReloadMenza] = useState(0);

  const [forms, setForms] = useState<Record<string, ApplicationForm>>({});

  const mounted = useRef(false);
  const readVersion = useRef(0);
  const pending = useRef(new Set<string>());
  const postControllers = useRef(new Set<AbortController>());

  const hasMenza =
    events?.some(event => event.type === "MunnaMenza") ?? false;

  const updateForm = (
    key: string,
    values: Partial<ApplicationForm>
  ) => {
    setForms(current => ({
      ...current,
      [key]: {
        ...EMPTY_FORM,
        ...current[key],
        ...values,
      },
    }));
  };

  // Update form visibility as registration opens or closes.
  useEffect(() => {
    mounted.current = true;

    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => {
      mounted.current = false;
      window.clearInterval(timer);
      postControllers.current.forEach(controller => controller.abort());
    };
  }, []);

  // Load the event calendar.
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const response = await fetch(EVENTS_URL, {
          headers: { Accept: "application/json" },
          cache: "no-cache",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (alive) {
          setEvents(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (alive) {
          setErr(error instanceof Error ? error.message : "fetch_failed");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  // Refresh participant lists every 30 seconds and when returning to the page.
  useEffect(() => {
    if (!hasMenza) return;

    const refresh = () => {
      if (pending.current.size === 0) {
        setReloadMenza(value => value + 1);
      }
    };

    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [hasMenza]);

  // Load the grouped participant lists from the simplified API.
  useEffect(() => {
    if (!hasMenza || pending.current.size > 0) return;

    const controller = new AbortController();
    const version = ++readVersion.current;
    let alive = true;

    (async () => {
      try {
        const response = await fetch(MENZA_GET_URL, {
          headers: { Accept: "application/json" },
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error("Invalid participant list");
        }

        if (alive && version === readVersion.current) {
          setMenzaEvents(data);
          setMenzaLoaded(true);
          setMenzaError(false);
        }
      } catch {
        if (alive && version === readVersion.current) {
          setMenzaError(true);
        }
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [hasMenza, reloadMenza]);

  const applyForMenza = async (ev: EventItem, key: string) => {
    if (pending.current.has(key)) return;

    const form = forms[key] ?? EMPTY_FORM;
    const start = eventDateTime(ev.date, ev.time)?.getTime();
    const currentTime = Date.now();

    const startMs = eventDateTime(ev.date, ev.time)?.getTime();
    // Recheck the window at submission, not only when rendering.
    if (
      startMs === undefined ||
      Date.now() >= startMs - 2 * HOUR
    ) {
      updateForm(key, {
        error: t(
          startMs === undefined
            ? "eventsPage.munnaMenza.timeUnavailable"
            : "eventsPage.munnaMenza.closed"
        ),
        success: false,
      });

      return;
    }

    if (!form.name.trim()) {
      updateForm(key, {
        error: t("eventsPage.munnaMenza.errors.invalid_name"),
        success: false,
      });

      return;
    }

    const request = {
      eventId: ev.id,
      date: ev.date,
      name: form.name.trim(),
      notes: form.notes.trim(),
    };

    pending.current.add(key);

    // Ignore GET responses that started before this submission.
    ++readVersion.current;

    updateForm(key, {
      sending: true,
      error: "",
      success: false,
    });

    const controller = new AbortController();
    postControllers.current.add(controller);

    try {
      const response = await fetch(MENZA_ADD_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);

        throw new Error(
          response.status === 409
            ? t("eventsPage.munnaMenza.errors.duplicate")
            : body?.message ||
                t("eventsPage.munnaMenza.errors.submit_failed")
        );
      }

      if (mounted.current) {
        const participant = {
          name: request.name,
          notes: request.notes,
        };

        setMenzaEvents(current => {
          const existing = current.find(
            item => item.eventId === ev.id && item.date === ev.date
          );

          if (!existing) {
            return [
              ...current,
              {
                eventId: ev.id,
                date: ev.date,
                participants: [participant],
              },
            ];
          }

          return current.map(item =>
            item === existing
              ? {
                  ...item,
                  participants: [
                    ...(item.participants ?? []),
                    participant,
                  ],
                }
              : item
          );
        });

        updateForm(key, {
          name: "",
          notes: "",
          success: true,
        });
      }
    } catch (error) {
      if (mounted.current) {
        updateForm(key, {
          error:
            error instanceof Error && !(error instanceof TypeError)
              ? error.message
              : t("eventsPage.munnaMenza.errors.submit_failed"),
        });
      }
    } finally {
      pending.current.delete(key);
      postControllers.current.delete(controller);

      if (mounted.current) {
        updateForm(key, { sending: false });

        // Also refresh after a duplicate or an uncertain network result.
        if (pending.current.size === 0) {
          setReloadMenza(value => value + 1);
        }
      }
    }
  };

  const list = useMemo(
    () =>
      (events ?? [])
        .map(ev => ({
          ev,
          start: eventDateTime(
            ev.date,
            ev.time ?? "00:00"
          )?.getTime(),
        }))
        .filter(item => item.start !== undefined && item.start >= now - HOUR)
        .sort((a, b) => (a.start ?? 0) - (b.start ?? 0))
        .map(item => item.ev),
    [events, now]
  );

  const fmtDate = (date: string) =>
    new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "short",
      timeZone: "UTC",
    }).format(new Date(`${date}T00:00:00Z`));

  const fmtTime = (time: string | null, date: string) => {
    const value = eventDateTime(date, time);

    return value
      ? new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: TZ,
        }).format(value)
      : null;
  };

  return (
    <div className="surface-body">
      <h1>{t("eventsPage.title")}</h1>
      <p className="events-intro">{t("eventsPage.intro")}</p>

      {loading && (
        <p style={{ opacity: 0.8 }}>{t("common.loading")}</p>
      )}

      {err && (
        <p className="error" role="alert">
          {t("common.error")}: {err}
        </p>
      )}

      {!loading && !err && (
        list.length === 0 ? (
          <p style={{ opacity: 0.8 }}>{t("eventsPage.empty")}</p>
        ) : (
          <div className="events-list">
            {list.map((ev, idx) => {
              const start = fmtTime(ev.time, ev.date);
              const end = fmtTime(ev.endTime, ev.date);

              const timeLabel =
                !ev.time && !ev.endTime
                  ? t("eventsPage.labels.allday", "All day")
                  : start && end
                    ? `${start} – ${end}`
                    : start
                      ? start
                      : end
                        ? `${t("eventsPage.labels.until", "until")} ${end}`
                        : t("eventsPage.labels.tbd", "TBD");

              const isMenza = ev.type === "MunnaMenza";

              const cardStyle =
                ev.type === "Movie Night"
                  ? "movie-night"
                  : isMenza
                    ? "munnamenza"
                    : idx % 2
                      ? "alt-yellow"
                      : "";

              const key = JSON.stringify([ev.id, ev.date]);
              const fieldId = `menza-${encodeURIComponent(ev.id)}-${ev.date}`;
              const form = forms[key] ?? EMPTY_FORM;

              const participants =
                menzaEvents.find(
                  item => item.eventId === ev.id && item.date === ev.date
                )?.participants ?? [];

              const startMs = eventDateTime(ev.date, ev.time)?.getTime();

              const registrationOpen =
                startMs !== undefined &&
                now < startMs - 2 * HOUR;

              return (
                <article
                  key={key}
                  className={`event-card ${cardStyle}`}
                >
                  <div className="event-head">
                    <h3 className="event-title">{ev.title}</h3>

                    <span
                      className={`event-type-badge type-${(
                        ev.type || "Other"
                      ).toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {ev.type}
                    </span>
                  </div>

                  <div className="event-meta">
                    <span className="kv">
                      <span className="k">
                        {t("eventsPage.labels.location")}:
                      </span>
                      <span>
                        {ev.location ?? t("eventsPage.labels.tbd", "TBD")}
                      </span>
                    </span>

                    <span className="kv">
                      <span className="k">
                        {t("eventsPage.labels.date")}:
                      </span>
                      <span>{fmtDate(ev.date)}</span>
                    </span>

                    <span className="kv">
                      <span className="k">
                        {t("eventsPage.labels.time")}:
                      </span>
                      <span>{timeLabel}</span>
                    </span>
                  </div>

                  <p className="event-desc">{ev.description}</p>

                  {isMenza && (
  <section className="munnamenza-panel">
    {/* Application form comes first */}
    {registrationOpen ? (
      <form
        className="munnamenza-form"
        aria-busy={form.sending}
        onSubmit={(e) => {
          e.preventDefault();
          void applyForMenza(ev, key);
        }}
      >
        <div className="munnamenza-field munna-name-field">
          <label htmlFor={`${fieldId}-name`}>
            {t("eventsPage.munnaMenza.name")}
          </label>

          <input
            id={`${fieldId}-name`}
            name="name"
            type="text"
            autoComplete="name"
            required
            value={form.name}
            disabled={form.sending}
            onChange={(e) =>
              updateForm(key, {
                name: e.target.value,
                error: "",
                success: false,
              })
            }
          />
        </div>

        <div className="munnamenza-field munna-name-field">
          <label htmlFor={`${fieldId}-notes`}>
            {t("eventsPage.munnaMenza.notes")}
          </label>

          <input
            id={`${fieldId}-notes`}
            name="notes"
            type="text"
            value={form.notes}
            disabled={form.sending}
            onChange={(e) =>
              updateForm(key, {
                notes: e.target.value,
                error: "",
                success: false,
              })
            }
          />
        </div>

        <button type="submit" disabled={form.sending}>
          {t(
            form.sending
              ? "eventsPage.munnaMenza.sending"
              : "eventsPage.munnaMenza.apply"
          )}
        </button>

        <p className="munnamenza-form-note">
          {t("eventsPage.munnaMenza.publicNote")}
        </p>
      </form>
    ) : (
      <p className="munnamenza-window-note">
        {t(
          startMs === undefined
            ? "eventsPage.munnaMenza.timeUnavailable"
            : "eventsPage.munnaMenza.closed"
        )}
      </p>
    )}

    {form.error && (
      <p className="munnamenza-error" role="alert">
        {form.error}
      </p>
    )}

    {form.success && (
      <p className="munnamenza-success" role="status">
        {t("eventsPage.munnaMenza.success")}
      </p>
    )}

    {/* Participants: collapsed by default, click to expand */}
    <details className="munnamenza-participants">
      <summary>
        {t("eventsPage.munnaMenza.heading")}
        {menzaLoaded && ` (${participants.length})`}
      </summary>

      {!menzaLoaded && !menzaError && (
        <p role="status">
          {t("eventsPage.munnaMenza.loading")}
        </p>
      )}

      {menzaError && (
        <p className="munnamenza-error" role="alert">
          {t("eventsPage.munnaMenza.loadError")}{" "}
          <button
            type="button"
            className="munnamenza-retry"
            onClick={() => setReloadMenza((value) => value + 1)}
          >
            {t("eventsPage.munnaMenza.retry")}
          </button>
        </p>
      )}

      {menzaLoaded && participants.length > 0 && (
        <ul className="munnamenza-applicants">
          {participants.map((participant, index) => (
            <li key={`${participant.name}-${index}`}>
              <span>{participant.name}</span>

              {participant.notes && (
                <span>{participant.notes}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {menzaLoaded && !menzaError && participants.length === 0 && (
        <p>{t("eventsPage.munnaMenza.empty")}</p>
      )}
    </details>
  </section>
)}
                </article>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}